

import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import juridentLogo from "../assets/jurident-logo.png";
import { Bell, X, Lock, LogOut } from "lucide-react";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { useInShell } from "../context/ShellContext";
import { useRef } from "react";
import useFeatureFlags from "../utils/useFeatureFlags";
import FeatureLockedDialog from "./FeatureLockedDialog";
import {
  collection,
  doc,
  deleteDoc,
  onSnapshot,
  query,
  orderBy,
  getDocs,
  getDoc,
  writeBatch,
} from "firebase/firestore";

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const inShell = useInShell();
  const { globalUser, logOut } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [userCategory, setUserCategory] = useState('');
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem("theme");
    return savedTheme ? savedTheme === "dark" : true;
  });

  const toggleDarkMode = () => {
    setIsDarkMode(prev => !prev);
  };
  const [hasInternshipAccess, setHasInternshipAccess] = useState(false);
  const [lockedFeatureName, setLockedFeatureName] = useState(null);
  const notificationPanelRef = useRef(null);
  const isClient = localStorage.getItem("userType") === "client";
  const { isEnabled } = useFeatureFlags(globalUser?.uid);
  const showLocked = (name) => (e) => {
    if (e) e.preventDefault();
    setLockedFeatureName(name);
  };

  // Helper function to determine link styles based on current path
  const getLinkClass = (path) => {
    return location.pathname === path
      ? "text-[#D4AF37]"
      : "text-white hover:text-[#D4AF37] transition-colors";
  };

  // Fetch user category and internship access
  useEffect(() => {
    if (inShell) return;
    const fetchUserCategoryAndSubscription = async () => {
      if (!globalUser) return;

      try {
        const userDocRef = doc(db, "lawyers", globalUser.uid);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          const data = userDoc.data();
          const category = data.userCategory || data.userCateogry || '';
          setUserCategory(category);

          // Check internship access for Law students
          if (category === 'Law student') {
            const subscriptionDocRef = doc(db, 'subscriptions', globalUser.uid);
            const subscriptionDoc = await getDoc(subscriptionDocRef);

            if (subscriptionDoc.exists()) {
              const subscriptionData = subscriptionDoc.data();
              const transactions = subscriptionData.transactions || [];

              // Check for active law student subscription
              const now = new Date();
              const hasActiveSubscription = transactions.some(transaction => {
                let endDate;
                if (transaction['end-time']?.toDate) {
                  endDate = transaction['end-time'].toDate();
                } else {
                  endDate = new Date(transaction['end-time']);
                }

                const isActive = endDate > now;
                const isLawStudent = transaction.type === 'lawStudent' ||
                                     transaction.category === 'lawStudent';

                return isActive && isLawStudent;
              });

              setHasInternshipAccess(hasActiveSubscription);
            } else {
              setHasInternshipAccess(false);
            }
          } else {
            setHasInternshipAccess(false);
          }
        }
      } catch (error) {
        console.error("Error fetching user category and subscription:", error);
      }
    };

    fetchUserCategoryAndSubscription();
  }, [globalUser]);

  // Fetch notifications in real-time when user is authenticated
  useEffect(() => {
    if (inShell) return;
    if (!globalUser) return;

    const userCollection = isClient ? "client" : "lawyers";
    const userDocRef = doc(db, userCollection, globalUser.uid);
    const notificationsRef = collection(userDocRef, "notifications");
    const notificationsQuery = query(
      notificationsRef,
      orderBy("timestamp", "desc")
    );

    const unsubscribe = onSnapshot(
      notificationsQuery,
      (snapshot) => {
        const notifs = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setNotifications(notifs);
      },
      (error) => {
        console.error("Error fetching notifications:", error);
      }
    );

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, [globalUser]);

  useEffect(() => {
      if (inShell) return;
      document.body.classList.remove("dark-mode", "light-mode");

      document.body.classList.add(
        isDarkMode ? "dark-mode" : "light-mode"
      );

      localStorage.setItem(
        "theme",
        isDarkMode ? "dark" : "light"
      );
    }, [isDarkMode, inShell]);

  // Handle clicks outside of notification panel
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isNotificationOpen &&
        notificationPanelRef.current &&
        !notificationPanelRef.current.contains(event.target)
      ) {
        setIsNotificationOpen(false);
      }
    };

    // Add event listener when notification panel is open
    if (isNotificationOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    // Clean up event listener
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isNotificationOpen]);

  // Function to delete a single notification
  const deleteNotification = async (notificationId) => {
    if (!globalUser) {
      console.error("User not authenticated. Cannot delete notification.");
      return;
    }
    try {
      const userCollection = isClient ? "client" : "lawyers";
      const userDocRef = doc(db, userCollection, globalUser.uid);
      const notificationsColRef = collection(userDocRef, "notifications");
      const notificationDocRef = doc(notificationsColRef, notificationId);
      await deleteDoc(notificationDocRef);
      console.log(`Notification ${notificationId} deleted.`);
    } catch (error) {
      console.error(`Error deleting notification ${notificationId}:`, error);
    }
  };

  // Function to delete all notifications
  const deleteAllNotifications = async () => {
    if (!globalUser) {
      console.error("User not authenticated. Cannot delete notifications.");
      return;
    }
    try {
      const userCollection = isClient ? "client" : "lawyers";
      const userDocRef = doc(db, userCollection, globalUser.uid);
      const notificationsColRef = collection(userDocRef, "notifications");
      const snapshot = await getDocs(notificationsColRef);
      const batch = writeBatch(db);
      snapshot.docs.forEach((doc) => {
        batch.delete(doc.ref);
      });
      await batch.commit();
      console.log("All notifications deleted.");
    } catch (error) {
      console.error("Error deleting all notifications:", error);
    }
  };

  // Function to handle notification tap/click
  const handleNotificationTap = (notification) => {
    const notificationType = notification.notificationType;

    // Handle app update notifications
    if (notificationType === 'app_update') {
      // Open app stores - web version just shows a message or could open web store
      window.open('https://play.google.com/store/apps/details?id=com.jurident.valsco&hl=en_IN', '_blank');
      return;
    }

    // Handle case-related notifications
    if (notificationType === 'case_shared' ||
        notificationType === 'case_access_removed' ||
        notificationType === 'event_reminder' ||
        notificationType === 'new_order') {

      const caseId = notification.caseId;
      const collection = notification.collection;

      if (!caseId || !collection) {
        console.error('Missing caseId or collection in notification metadata');
        return;
      }

      // Close notification panel
      setIsNotificationOpen(false);

      // Navigate to case details based on collection
      if (collection === 'PendingCases') {
        navigate(`/portal/case/${caseId}`, { state: { status: 'PendingCases' } });
      } else if (collection === 'DisposedCases') {
        navigate(`/portal/case/${caseId}`, { state: { status: 'DisposedCases' } });
      } else {
        // Default navigation
        navigate(`/portal/case/${caseId}`);
      }
    }
  };

  // Inside the CRM shell the persistent <Topbar/> replaces this legacy navbar.
  if (inShell) return null;

  return (
    <nav className="px-4 sm:px-6 py-4 z-[1000] backdrop-blur-md shadow-md sticky w-full top-0">
      <div className="flex items-center justify-between max-w-7xl mx-auto">
        {/* Logo */}
        <div className="flex-none">
          <Link to="/portal/Dashboard">
            <img src={juridentLogo} alt="Jurident" className="h-12 w-auto" />
          </Link>
        </div>

        {/* Desktop Navigation */}
        <div className="hidden md:flex gap-6 items-center">
          <Link to="/portal/Dashboard" className={getLinkClass("/Dashboard")}>
            Home
          </Link>
          <Link
            to="/portal/calendar"
            onClick={!isEnabled('calendar') ? showLocked('Calendar') : undefined}
            className={`${getLinkClass("/calendar")} flex items-center gap-1`}
          >
            Calendar
            {!isEnabled('calendar') && <Lock className="w-4 h-4 text-[#C99F4A]" />}
          </Link>
          <Link
            to="/portal/news"
            onClick={!isEnabled('news') ? showLocked('News') : undefined}
            className={`${getLinkClass("/news")} flex items-center gap-1`}
          >
            News
            {!isEnabled('news') && <Lock className="w-4 h-4 text-[#C99F4A]" />}
          </Link>
          <Link to="/portal/aboutus" className={getLinkClass("/Aboutus")}>
            About Us
          </Link>
          {!isClient && (
            <Link
              to="/portal/bareAct"
              onClick={!isEnabled('bareActs') ? showLocked('Bare Acts') : undefined}
              className={`${getLinkClass("/bareAct")} flex items-center gap-1`}
            >
              Bare Act PDF
              {!isEnabled('bareActs') && <Lock className="w-4 h-4 text-[#C99F4A]" />}
            </Link>
          )}
          {userCategory === 'Law student' && (
            <Link to="/portal/internship-form" className={`${getLinkClass("/internship-form")} relative flex items-center gap-1`}>
              Internship Form
              {!hasInternshipAccess && (
                <Lock className="w-4 h-4 text-[#C99F4A]" />
              )}
            </Link>
          )}
          <Link to="/portal/subscription" className={getLinkClass("/subscription")}>
            Subscription
          </Link>
          <div className="relative group">
            <Link to="/portal/account" className={`${getLinkClass("/account")} flex items-center gap-1`}>
              Account
            </Link>
            {/* Dropdown on hover */}
            <div className="absolute top-full right-0 pt-2 hidden group-hover:block z-50">
              <div className="account-dropdown bg-[#1E1E45] border border-gray-700 rounded-lg shadow-xl py-1 min-w-[180px]">
                <button
                  onClick={toggleDarkMode}
                  className="flex items-center gap-2 px-4 py-2 text-white hover:bg-gray-800 transition-colors text-sm w-full text-left"
                >
                  {isDarkMode ? "Light Mode" : "Dark Mode"}
                </button>

                <button
                  onClick={() => {
                    logOut();
                    navigate("/portal/onboarding");
                  }}
                  className="flex items-center gap-2 px-4 py-2 text-white hover:text-red-400 hover:bg-gray-800 transition-colors text-sm w-full text-left"
                > 
                  <LogOut className="w-4 h-4 text-white" />
                  Logout
                </button>

              </div>
            </div>
          </div>
          <button
            onClick={() => setIsNotificationOpen(true)}
            className="relative"
          >
            <Bell className="w-6 h-6 text-white" />
            {notifications.filter((n) => !n.read).length > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                {notifications.filter((n) => !n.read).length}
              </span>
            )}
          </button>
        </div>

        {/* Mobile: Burger Menu + Logout icon */}
        <div className="md:hidden flex items-center gap-2">
        <button
          className="text-white p-2"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            {isMenuOpen ? (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            ) : (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            )}
          </svg>
        </button>
          <button
            onClick={() => { logOut(); navigate("/portal/onboarding"); }}
            className="text-white p-2 hover:text-red-400 transition-colors"
            title="Logout"
          >
            <LogOut className="w-5 h-5" />
          </button>
        </div>

        {/* Mobile Menu Dropdown */}
        {isMenuOpen && (
          <div className="absolute top-full left-0 right-0 bg-[#0B0B26] z-[1000] shadow-lg md:hidden">
            <div className="flex flex-col py-2 px-4 space-y-3">
              <Link
                to="/portal/Dashboard"
                className={`${getLinkClass("/Dashboard")} py-2`}
                onClick={() => setIsMenuOpen(false)}
              >
                Home
              </Link>
              <Link
                to="/portal/calendar"
                className={`${getLinkClass("/calendar")} py-2 flex items-center gap-2`}
                onClick={(e) => {
                  if (!isEnabled('calendar')) { showLocked('Calendar')(e); return; }
                  setIsMenuOpen(false);
                }}
              >
                Calendar
                {!isEnabled('calendar') && <Lock className="w-4 h-4 text-[#C99F4A]" />}
              </Link>
              <Link
                to="/portal/news"
                className={`${getLinkClass("/news")} py-2 flex items-center gap-2`}
                onClick={(e) => {
                  if (!isEnabled('news')) { showLocked('News')(e); return; }
                  setIsMenuOpen(false);
                }}
              >
                News
                {!isEnabled('news') && <Lock className="w-4 h-4 text-[#C99F4A]" />}
              </Link>
              <Link
                to="/portal/aboutus"
                className={`${getLinkClass("/aboutus")} py-2`}
                onClick={() => setIsMenuOpen(false)}
              >
                About Us
              </Link>
              {!isClient && (
                <Link
                  to="/portal/bareAct"
                  className={`${getLinkClass("/bareAct")} py-2 flex items-center gap-2`}
                  onClick={(e) => {
                    if (!isEnabled('bareActs')) { showLocked('Bare Acts')(e); return; }
                    setIsMenuOpen(false);
                  }}
                >
                  Bare Act PDF
                  {!isEnabled('bareActs') && <Lock className="w-4 h-4 text-[#C99F4A]" />}
                </Link>
              )}
              {userCategory === 'Law student' && (
                <Link
                  to="/portal/internship-form"
                  className={`${getLinkClass("/internship-form")} py-2 flex items-center gap-2`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  Internship Form
                  {!hasInternshipAccess && (
                    <Lock className="w-4 h-4 text- #C99F4A" />
                  )}
                </Link>
              )}
              <Link
                to="/portal/subscription"
                className={`${getLinkClass("/subscription")} py-2`}
                onClick={() => setIsMenuOpen(false)}
              >
                Subscription
              </Link>
              <Link
                to="/portal/account"
                className={`${getLinkClass("/account")} py-2`}
                onClick={() => setIsMenuOpen(false)}
              >
                Account
              </Link>
              <button
                onClick={() => {
                  setIsNotificationOpen(true);
                  setIsMenuOpen(false);
                }}
                className="text-left py-2 text-white hover:text-[#D4AF37] transition-colors"
              >
                Notifications
              </button>
            </div>
          </div>
        )}

        {/* Notification Modal */}
        {isNotificationOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-end"
          >
            <div
              ref={notificationPanelRef}
              className="bg-gray-100 w-80 min-h-[50vh] max-h-[80vh] p-4 overflow-y-auto rounded-l-lg shadow-lg"
            >
              <div className="bg-gray-200 p-3 rounded-t-lg flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-800">
                  Notifications
                </h2>
                <button
                  onClick={() => {
                    deleteAllNotifications();
                    setIsNotificationOpen(false);
                  }}
                  className="text-gray-600 hover:text-gray-800 p-1"
                  aria-label="Close notifications"
                >
                  <X size={20} />
                </button>
              </div>
              {notifications.length > 0 ? (
                notifications.map((notif) => {
                  const isCaseNotification =
                    notif.notificationType === 'case_shared' ||
                    notif.notificationType === 'case_access_removed' ||
                    notif.notificationType === 'event_reminder' ||
                    notif.notificationType === 'new_order' ||
                    notif.notificationType === 'app_update';

                  return (
                    <div
                      key={notif.id}
                      onClick={isCaseNotification ? () => handleNotificationTap(notif) : undefined}
                      className={`relative bg-white rounded-lg p-3 mb-3 shadow-sm hover:shadow-md transition-shadow ${
                        notif.read ? "" : "border-l-4 border-blue-500"
                      } ${isCaseNotification ? "cursor-pointer hover:bg-gray-50" : ""}`}
                    >
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteNotification(notif.id);
                        }}
                        className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
                        aria-label="Delete notification"
                      >
                        <X size={16} />
                      </button>
                      <h3 className="text-md font-semibold text-gray-800">
                        {notif.title}
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">{notif.body}</p>
                      <small className="text-xs text-gray-400 mt-2 block">
                        {notif.timestamp?.toDate().toLocaleString()}
                      </small>
                      {isCaseNotification && (
                        <div className="flex items-center mt-2 text-[#D4AF37] text-xs italic">
                          <svg
                            className="w-3 h-3 mr-1"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122"
                            />
                          </svg>
                          <span>
                            {notif.notificationType === 'app_update'
                              ? 'Tap to update app'
                              : 'Tap to view case details'}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <p className="text-gray-500 text-center py-4">
                  No notifications available.
                </p>
              )}
            </div>
          </div>
        )}
      </div>
      <FeatureLockedDialog
        isOpen={!!lockedFeatureName}
        featureName={lockedFeatureName}
        onClose={() => setLockedFeatureName(null)}
      />
    </nav>
  );
};

export default Navbar;
